import { getRawValue } from './useDriverLeaderboardCells';

const isMissing = (value: unknown): boolean =>
  value === undefined || value === null || value === '' || value === '-';

// Ascending comparison of two rank-field values. Missing values always sort
// last (whatever the direction), matching search2.
const compareValues = ({
  a,
  b,
  sort,
}: {
  a: unknown;
  b: unknown;
  sort: SortBy;
}): number => {
  const aMissing = isMissing(a);
  const bMissing = isMissing(b);
  if (aMissing || bMissing) {
    return aMissing === bMissing ? 0 : aMissing ? 1 : -1;
  }
  const asc =
    typeof a === 'number' && typeof b === 'number'
      ? a - b
      : String(a).localeCompare(String(b), undefined, { numeric: true });
  return sort === 'desc' ? -asc : asc;
};

/*
 * A leaderboard's dynamic rank: documents ordered by the slide's sort fields
 * (the first decides, the rest break ties), with standard competition ranking
 * — documents equal on every sort field share a rank (1, 2, 2, 4).
 *
 * Ranks come from the values alone, never the order the documents arrive in,
 * so however the rows end up displayed, rank 1 stays rank 1. Returns one rank
 * per document, aligned with the input array.
 */
const computeDynamicRanks = ({
  documents,
  sortBy,
}: {
  documents: ElasticDocument[];
  sortBy: SortField[];
}): number[] => {
  const compare = (a: ElasticDocument, b: ElasticDocument): number => {
    for (const { field, sort } of sortBy) {
      const result = compareValues({
        a: getRawValue({ field, document: a }),
        b: getRawValue({ field, document: b }),
        sort,
      });
      if (result !== 0) {
        return result;
      }
    }
    return 0;
  };

  const order = documents
    .map((document, index) => ({ document, index }))
    .sort((a, b) => compare(a.document, b.document));

  const ranks: number[] = new Array(documents.length);
  order.forEach(({ document, index }, position) => {
    const previous = order[position - 1];
    ranks[index] =
      previous && compare(previous.document, document) === 0
        ? ranks[previous.index]
        : position + 1;
  });
  return ranks;
};

export default computeDynamicRanks;
